import { useCallback, useEffect, useRef, useState } from 'react'
import { observer, Observer, useLocalObservable } from 'mobx-react-lite'
import { autorun, reaction, runInAction } from 'mobx'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button } from 'ui-components-web'
import XmarkIcon from '@ui-components-3/icons/lib/solid/Xmark'
import classNames from '@ui-components-3/ui/lib/utils/classNames'
import withResolution from 'hoc/withResolution'
import useVisit from 'hooks/useVisit'
import GlobalEvents from 'singletons/GlobalEvents'
import Modals from 'singletons/Modals'
import User from 'singletons/User'
import VideoChatModel from 'models/VideoChat'
import VisitPanel from 'components/Dashboard/Main/Visit/VisitPanel'
import TabPanel from 'components/Dashboard/Main/Visit/TabPanel'
import VideoChatView from 'components/UI/videoChat'
import Chat from './Chat'
import useStyles from './styles'
import { useMediaQuery } from 'react-responsive'

import { MEMBER_INFO_COLLAPSE_BOUND } from 'constants/breakpoints'
import { DASHBOARD } from 'constants/routing'
import isVisitActual from 'util/isVisitActual'
import MessagesList from './Chat/MessagesList'
import MessageTextarea from './Chat/MessageTextarea'
import ChatMessageNotifier from 'singletons/ChatMessageNotifier'
import SideNavSingleton from 'singletons/SideNav'
import getInitialChatMessage from 'util/getInitialChatMessage'
import { isSuccessVisit } from 'constants/visit-types'
import DailyVideoProvider from 'components/UI/videoChat/DailyVideoChat/DailyVideoProvider'
import { PHONE_CALL_STATUS } from 'constants/phoneCallStatuses'
import DailyConnection from 'components/UI/DailyConnection'
import { useCrisisStream } from 'hooks/crisis/useCrisisStream'

/*
  How many percent of video wrapper must be visible in order for big View to be rendered.
  If less - small view is rendered.
*/
const THRESHOLD = 0.5

const ConnectedVisitPanel = observer(({ visit, store, scrollContainerToTop, ...props }) => {
  const classes = useStyles()
  if (!visit) {
    return null
  }

  return (
    <VisitPanel
      {...props}
      store={store}
      leftBlock={
        !store.video.active || store.bigView ? null : (
          <VideoChatView className={classes.compactVideo} store={store} compact />
        )
      }
      scrollContainerToTop={scrollContainerToTop}
    />
  )
})
async function isWebcamAvailable() {
  try {
    const md = navigator.mediaDevices
    if (!md || !md.enumerateDevices) {
      return false
    }
    const devices = await md.enumerateDevices()
    return devices.some((device) => 'videoinput' === device.kind)
  } catch {
    return false
  }
}
const Visit = ({ isMobile }) => {
  const classes = useStyles()
  const visit = useVisit()
  const { visitId } = useParams()
  const navigate = useNavigate()

  useCrisisStream()

  const isSuccess = visit?.isSuccess

  const visitTitle = isSuccess ? 'Session' : 'Visit'

  const store = useLocalObservable(() => ({
    bigView: true,
    memberPanelVisible: true,
    visit,
    isMobile,
    chatDialogVisible: false,
    videoStartInProgress: false,
    chatStartInProgress: false,

    hideChat() {
      this.chatDialogVisible = false
    },
    setMemberPanelVisibility(visible) {
      this.memberPanelVisible = visible
    },
    showChat() {
      this.chatDialogVisible = true
    },
    toggleChat() {
      if (this.chatDialogVisible) {
        this.hideChat()
      } else {
        this.showChat()
      }
    },
    setView(isBig) {
      this.bigView = isBig
    },
    setIsMobile(isMobile) {
      this.isMobile = isMobile
    },
    video: new VideoChatModel(),
    setVisit(visit) {
      this.visit = visit
    },
    get disableStart() {
      return (
        this.videoStartInProgress ||
        this.visit?.phoneCall?.status === PHONE_CALL_STATUS.INPROGRESS ||
        this.chatStartInProgress
      )
    },
    *startVideo() {
      this.videoStartInProgress = true
      try {
        const canStart = yield isWebcamAvailable()
        if (canStart) {
          if (!this.visit.started) {
            yield this.visit.start()
          }
          const connectId = yield this.visit.startVideoCall()
          if (this.isMobile) {
            this.setMemberPanelVisibility(false)
          }
          yield this.video.connect(connectId, this.visit.visitId)
        } else {
          GlobalEvents.emit('visitStartVideo', {
            status: 'error',
            description: 'No webcam available',
          })
        }
      } finally {
        this.videoStartInProgress = false
      }
    },
    async stopVideo() {
      this.video.disconnect()
      this.visit?.phoneCall?.end?.()
      if (this.isMobile) {
        this.setMemberPanelVisibility(true)
      }
    },
    *startChat() {
      this.chatStartInProgress = true
      try {
        if (!this.visit.started) {
          yield this.visit.start()
        }
      } finally {
        this.chatStartInProgress = false
      }
    },
  }))
  /* sync with user store for autoLogout modal */
  useEffect(() => {
    User.setCurrentVideo(store.video)
    return () => User.setCurrentVideo(null)
  }, [])
  useEffect(
    () =>
      reaction(
        () => (store.video.active ? store.video.participants?.length : -1),
        (quantity, prevQuantity) => {
          if (quantity === 0 && prevQuantity > quantity) {
            /* To prevent double open case when someone leave from call 
            while provider already opened this modal */
            if (!Modals.list.some((m) => m.type === 'confirmEndCall')) {
              Modals.open('confirmEndCall', {
                visit: store.visit,
                onConfirm: () => store.stopVideo(),
              })
            }
          }
        },
        /* To prevent modal popup caused by internet problems (e.g when someone
          dropped for 0.5 sec then back) */
        { delay: 1000 },
      ),
    [],
  )

  const sendProviderToDashboard = useCallback(
    (message) => {
      Modals.closeAll()
      toast.success(message)
      navigate(DASHBOARD)
    },
    [navigate],
  )
  /* Unique case, but... */
  useEffect(() => {
    async function effect() {
      store.setVisit(visit)
      if (process.env.NODE_ENV !== 'production') {
        console.log('Last visit: ', visit)
      }
      const joinedVisitRecord = await User.getVisitByPendingVisitId(visitId)
      await store.visit?.fetchCancellationReasons()
      if (process.env.NODE_ENV !== 'production') {
        console.log('Joined visit record', joinedVisitRecord)
      }

      if (joinedVisitRecord) {
        const joinedVisitTitle = isSuccessVisit(joinedVisitRecord.visitType) ? 'Session' : 'Visit'
        if (!isVisitActual(joinedVisitRecord)) {
          if (joinedVisitRecord.status === 'completed') {
            sendProviderToDashboard(`${joinedVisitTitle} completed`)
          } else sendProviderToDashboard(`${joinedVisitTitle} is not available`)
        } else if (visit) {
          visit.merge(joinedVisitRecord)
        }
      }
    }

    effect()

    return () => {
      /* avoid storing too old messages */
      visit?.chat.clear()
    }
  }, [visit])

  useEffect(() => {
    if (visit) {
      return reaction(
        () => visit.modality !== 'chat' && visit.chat.fetched && visit.chat.lastReceivedMessage,
        (msg, prevMsg) => msg && prevMsg && store.showChat(),
      )
    }
  }, [visit])

  useEffect(() => {
    let disposeRunner = null
    if (visit) {
      disposeRunner = autorun(() => {
        ChatMessageNotifier.toggle(visit.pendingVisitId, visit.modality === 'chat' || store.chatDialogVisible)
      })

      return () => {
        disposeRunner()
        ChatMessageNotifier.toggle(visit.pendingVisitId, false)
      }
    }
  }, [visit])

  useEffect(
    () =>
      autorun(() => {
        if (visit && !visit.isViewable && !visit.wasCancelledByProvider) {
          sendProviderToDashboard(`${visitTitle} was cancelled`)
        }
      }),
    [sendProviderToDashboard, visit, visitTitle],
  )

  const scrollerRef = useRef()
  const [videoRoomEl, setVideoRoomEl] = useState()

  useEffect(() => {
    return () => store.visit?.phoneCall?.end?.()
  }, [])

  useEffect(() => {
    if (videoRoomEl && !isMobile) {
      const O = new IntersectionObserver(
        (entry) => {
          store.setView(entry[0].isIntersecting)
        },
        {
          root: scrollerRef.current,
          threshold: THRESHOLD,
        },
      )
      O.observe(videoRoomEl)
      return () => O.disconnect()
    }
  }, [videoRoomEl, isMobile])

  useEffect(() => {
    /* TODO: dirty */
    store.setIsMobile(isMobile)
    if (isMobile) {
      store.setView(true)
    } else {
      store.setMemberPanelVisibility(true)
    }
  }, [isMobile])

  useEffect(() => {
    const disposeNavCollapse = autorun(() => {
      if (store?.visit?.status === 'in-progress') {
        runInAction(() => {
          SideNavSingleton.setCollapsable(true)
          SideNavSingleton.setIsOpen(false)
        })
      }
    })
    return () => {
      disposeNavCollapse()
      SideNavSingleton.setCollapsable(false)
      SideNavSingleton.setIsOpen(true)
    }
  }, [store])

  useEffect(
    () =>
      autorun(() => {
        if (store.visit) {
          const { modality, chat, providerName, isLocationAbroad } = store.visit
          if (modality === 'chat' && chat.length === 0 && chat.mySenderId) {
            chat.sendMessage(getInitialChatMessage({ providerName, isLocationAbroad }))
          }
        }
      }),
    [store],
  )

  const scrollContainerToTop = () => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = 0
    }
  }

  const isChatVisit = visit && visit.modality === 'chat'
  const isTabletOrMobile = useMediaQuery({ query: `(max-width: 920px)` })
  useEffect(() => {
    if (isChatVisit && !isTabletOrMobile) store.showChat()
  }, [isChatVisit, isTabletOrMobile])

  return (
    <DailyVideoProvider video={store.video}>
      <DailyConnection />
      <ConnectedVisitPanel visit={visit} store={store} scrollContainerToTop={scrollContainerToTop} />
      <div
        ref={scrollerRef}
        className={classNames(
          'flex overflow-y-auto min-h-0 shrink grow basis-0 w-full',
          isChatVisit ? 'relative' : 'flex-col flex-nowrap',
        )}
        onDoubleClick={
          process.env.NODE_ENV !== 'production'
            ? (e) => (e.ctrlKey || e.metaKey || e.altKey) && store.showChat()
            : undefined
        }
      >
        {isChatVisit ? (
          <div className="flex overflow-hidden grow">
            <Observer>
              {() => (
                <div
                  className={classNames(
                    'w-full overflow-auto flex flex-col',
                    isTabletOrMobile && store.chatDialogVisible && 'overflow-visible',
                  )}
                >
                  <TabPanel
                    areAdditionalTabsVisible={visit && visit.started}
                    toggleChat={store.toggleChat}
                    hideChat={store.hideChat}
                    chatDialogVisible={store.chatDialogVisible}
                    modality={visit.modality}
                    inProgress={store.visit?.status === 'in-progress'}
                  />
                </div>
              )}
            </Observer>
            <Observer>
              {() =>
                store.chatDialogVisible && isChatVisit && visit.showChatWindow ? (
                  <div
                    className={classNames(
                      'h-full min-w-[480px]',
                      isTabletOrMobile && 'w-full min-w-[300px] fixed bg-white mt-12 z-10',
                    )}
                  >
                    <div className="flex flex-col border-solid border border-neutral-300 border-b-0">
                      <div className="flex bg-white p-4 justify-between items-center border-solid border border-neutral-200 h-12 drop-shadow-lg">
                        <h5 className="font-semibold text-neutral-500 p-2">Chat</h5>
                        {visit.status === 'in-progress' ? (
                          <Button
                            type="button"
                            variant={'outlined'}
                            color={'inherit'}
                            size={'medium'}
                            className={classes.cancelButton}
                            onClick={() =>
                              Modals.open('confirmEndCall', {
                                visit,
                              })
                            }
                          >
                            <XmarkIcon className="h-4 w-4 mr-1 text-fuchsia-800" aria-hidden="true" />
                            END CHAT
                          </Button>
                        ) : null}
                      </div>
                      <div className="flex flex-col h-full">
                        <div className={classNames(classes.chatRoomWrapper, !store.memberPanelVisible && 'h-[40vh]')}>
                          <MessagesList visit={visit} className="max-w-[28em]" />
                        </div>
                        <div className="flex justify-center h-full p-6">
                          <MessageTextarea visit={visit} className="max-w-[25em] w-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null
              }
            </Observer>
          </div>
        ) : (
          <>
            <Observer>
              {() =>
                !!store.video.active && (
                  <div className={classes.videoRoomWrapper} ref={setVideoRoomEl}>
                    {!!store.bigView && <VideoChatView store={store} />}
                  </div>
                )
              }
            </Observer>
            <Observer>{() => <TabPanel areAdditionalTabsVisible={visit && visit.started} />}</Observer>
            <Observer>
              {() => (store.chatDialogVisible ? <Chat visit={visit} onClose={() => store.hideChat()} /> : null)}
            </Observer>
          </>
        )}
      </div>
    </DailyVideoProvider>
  )
}
export default withResolution(MEMBER_INFO_COLLAPSE_BOUND)(observer(Visit))
